/**
 * SMS Consent Status Enum
 *
 * Used for storing consent status directly in patient records for quick access.
 * This replaces the previous boolean consent flags with clear varchar status values
 *
 * The PatientConsent table maintains detailed audit trail while these fields
 * provide the current effective status for fast queries.
 */
export enum SmsConsentStatus {
  /** Patient has explicitly opted in via formal consent process */
  OPTED_IN = 'OPTED_IN',

  /** Patient has explicitly opted out or consent has been revoked */
  OPTED_OUT = 'OPTED_OUT',

  /** Patient opted out via carrier STOP message - requires patient to send START */
  CARRIER_OPTED_OUT = 'CARRIER_OPTED_OUT',

  /** Default state - implied consent for existing patients, TRANSACTIONAL ONLY */
  IMPLIED = 'IMPLIED',

  /** Double opt-in process initiated but not yet confirmed */
  PENDING_CONFIRMATION = 'PENDING_CONFIRMATION',

  /** Marketing consent prohibited (used for minors) */
  PROHIBITED = 'PROHIBITED',
}

/**
 * Core types for consent status (UI-agnostic)
 */
export interface ConsentStatusInfo {
  label: string;
  color: 'green' | 'amber' | 'red' | 'blue' | 'gray';
  description: string;
}

export interface ConsentOverallStatusInfo {
  label: string;
  color: 'green' | 'amber' | 'red' | 'blue' | 'gray';
  description: string;
  warnings: string[];
  canSendTransactional: boolean;
  canSendMarketing: boolean;
}

/**
 * Core helper functions for consent status logic (UI-agnostic)
 */
export class SmsConsentHelper {
  /**
   * Check if transactional messaging is allowed
   */
  static canSendTransactional(status: SmsConsentStatus): boolean {
    return [SmsConsentStatus.OPTED_IN, SmsConsentStatus.IMPLIED].includes(status);
  }

  /**
   * Check if this is a carrier-level opt-out (requires patient to send START)
   */
  static isCarrierOptOut(status: SmsConsentStatus): boolean {
    return status === SmsConsentStatus.CARRIER_OPTED_OUT;
  }

  /**
   * Check if marketing messaging is allowed
   */
  static canSendMarketing(status: SmsConsentStatus): boolean {
    return status === SmsConsentStatus.OPTED_IN;
  }

  /**
   * Get basic info for consent status (no icons)
   */
  static getStatusInfo(status: SmsConsentStatus): ConsentStatusInfo {
    switch (status) {
      case SmsConsentStatus.OPTED_IN:
        return {
          label: 'Consented',
          color: 'green',
          description: 'Patient has given explicit consent',
        };
      case SmsConsentStatus.IMPLIED:
        return {
          label: 'Implied Consent',
          color: 'amber',
          description: 'HIPAA implied consent - limited messages allowed',
        };
      case SmsConsentStatus.OPTED_OUT:
        return {
          label: 'Not Consented',
          color: 'red',
          description: 'Patient has not consented to these messages',
        };
      case SmsConsentStatus.CARRIER_OPTED_OUT:
        return {
          label: 'Carrier Block',
          color: 'red',
          description: 'Patient sent STOP - they must text START to resume',
        };
      case SmsConsentStatus.PENDING_CONFIRMATION:
        return {
          label: 'Awaiting Confirmation',
          color: 'amber',
          description: 'Waiting for patient to confirm consent',
        };
      case SmsConsentStatus.PROHIBITED:
        return {
          label: 'Not Allowed',
          color: 'red',
          description: 'Marketing messages not permitted for minors',
        };
      default:
        return {
          label: 'Unknown',
          color: 'gray',
          description: 'Unknown consent status',
        };
    }
  }

  /**
   * Get overall consent status for both transactional and marketing
   */
  static getOverallStatusInfo(
    transactionalStatus: SmsConsentStatus,
    marketingStatus: SmsConsentStatus,
    type: 'transactional' | 'marketing' | 'both' = 'both',
    isMinor: boolean = false,
    guardianName?: string
  ): ConsentOverallStatusInfo {
    const warnings: string[] = [];

    if (isMinor) {
      warnings.push(`Messages will be sent to ${guardianName || 'parent/guardian'}`);

      if (type === 'marketing' || type === 'both') {
        warnings.push('Marketing texts are prohibited for minors');
      }
    }

    const canSendTransactional = this.canSendTransactional(transactionalStatus);
    const canSendMarketing = this.canSendMarketing(marketingStatus) && !isMinor;

    // Return specific type status
    if (type === 'transactional') {
      const info = this.getStatusInfo(transactionalStatus);

      return {
        ...info,
        warnings,
        canSendTransactional,
        canSendMarketing: false,
      };
    }

    if (type === 'marketing') {
      if (isMinor) {
        return {
          label: 'Prohibited',
          color: 'red',
          description: 'Marketing texts prohibited for minors',
          warnings,
          canSendTransactional: false,
          canSendMarketing: false,
        };
      }

      const info = this.getStatusInfo(marketingStatus);

      return {
        ...info,
        warnings,
        canSendTransactional: false,
        canSendMarketing,
      };
    }

    // Handle 'both' type - determine combined status
    // If both are opted out, show red
    if (
      transactionalStatus === SmsConsentStatus.OPTED_OUT &&
      marketingStatus === SmsConsentStatus.OPTED_OUT
    ) {
      return {
        label: 'Messaging Blocked',
        color: 'red',
        description: 'All messaging is blocked',
        warnings,
        canSendTransactional: false,
        canSendMarketing: false,
      };
    }

    // If only transactional is opted out, show red (can't send any messages)
    if (transactionalStatus === SmsConsentStatus.OPTED_OUT) {
      return {
        label: 'Messaging Blocked',
        color: 'red',
        description: 'Patient has opted out of messages',
        warnings,
        canSendTransactional: false,
        canSendMarketing: false,
      };
    }

    // If only marketing is opted out but transactional is allowed, show amber
    if (marketingStatus === SmsConsentStatus.OPTED_OUT && canSendTransactional) {
      return {
        label: 'Limited Messaging',
        color: 'amber',
        description: 'Transactional messages only',
        warnings,
        canSendTransactional,
        canSendMarketing: false,
      };
    }

    if (
      transactionalStatus === SmsConsentStatus.OPTED_IN &&
      (marketingStatus === SmsConsentStatus.OPTED_IN || isMinor)
    ) {
      return {
        label: 'Full Messaging',
        color: 'green',
        description: 'All message types allowed',
        warnings,
        canSendTransactional,
        canSendMarketing,
      };
    }

    // If transactional is IMPLIED (allowed) and marketing is not consented
    if (
      transactionalStatus === SmsConsentStatus.IMPLIED &&
      !this.canSendMarketing(marketingStatus)
    ) {
      return {
        label: 'Basic Messaging',
        color: 'green',
        description: 'Transactional messages allowed',
        warnings,
        canSendTransactional: true,
        canSendMarketing: false,
      };
    }

    if (
      [SmsConsentStatus.IMPLIED, SmsConsentStatus.PENDING_CONFIRMATION].includes(
        transactionalStatus
      ) ||
      [SmsConsentStatus.IMPLIED, SmsConsentStatus.PENDING_CONFIRMATION].includes(marketingStatus)
    ) {
      return {
        label: 'Basic Messaging',
        color: 'amber',
        description: 'Essential messages only',
        warnings,
        canSendTransactional,
        canSendMarketing,
      };
    }

    return {
      label: 'Unknown',
      color: 'gray',
      description: 'Unknown consent status',
      warnings,
      canSendTransactional,
      canSendMarketing,
    };
  }

  /**
   * Determine if consent management is required before messaging
   */
  static requiresConsentManagement(
    transactional: SmsConsentStatus,
    marketing?: SmsConsentStatus
  ): boolean {
    return [SmsConsentStatus.OPTED_OUT, SmsConsentStatus.PENDING_CONFIRMATION].includes(
      transactional
    );
  }
}
